import { useMutation } from '@apollo/client';
import { IGeoUploadFormValues } from 'FeatureModules/DataImport/schemas/geoUpload.schema';
import { IDataset } from 'FeatureModules/DataImport/types/dataset.types';
import { DELETE_DOCUMENTS } from 'graphql/secure/mutations';
import {
  CREATE_DATASET,
  CREATE_GEO_ENTITIES_EXPORT,
  DELETE_DATASET,
  PRE_PROCESS_DATASET,
  PROCESS_DATASET,
  UPDATE_DATASET,
} from 'graphql/secure/mutations/dataset.mutations';
import {
  ICreateDatasetInput,
  ICreateGeoEntitiesExportInput,
  IPreProcessDatasetInput,
  IPreProcessDatasetPayload,
  IProcessDatasetInput,
  IProcessDatasetPayload,
  IUpdateDatasetInput,
} from 'graphql/secure/mutations/types/dataset-mutation.types';
import useMessages from 'hooks/useMessages';

const useDatasetMutation = () => {
  const [deleteDocument] = useMutation<{ deleteDocument: { ids: string[] } }>(DELETE_DOCUMENTS);
  const { setErrorMessage, setSuccessMessage } = useMessages();

  const [_createDataset] = useMutation<
    { createDataset: { dataset: IDataset } },
    { input: ICreateDatasetInput }
  >(CREATE_DATASET);

  const [_updateDataset] = useMutation<
    { updateDataset: { dataset: IDataset } },
    { id: string; input: IUpdateDatasetInput }
  >(UPDATE_DATASET);

  const [_deleteDataset] = useMutation<{ deleteDataset: { id: string } }, { id: string }>(
    DELETE_DATASET,
    {
      onCompleted: () => {
        setSuccessMessage('Dataset deleted successfully.');
      },
      onError: () => {
        setErrorMessage('Error deleting dataset');
      },
      refetchQueries: ['GetDatasets'],
    }
  );

  const [_processDataset] = useMutation<
    { processDataset: IProcessDatasetPayload },
    { input: IProcessDatasetInput }
  >(PROCESS_DATASET);

  const [_preProcessDataset] = useMutation<
    { preProcessDataset: IPreProcessDatasetPayload },
    { input: IPreProcessDatasetInput }
  >(PRE_PROCESS_DATASET);

  const startDatasetProcessing = async (input: IProcessDatasetInput) => {
    return await _processDataset({
      variables: {
        input,
      },
      refetchQueries: [
        'GetGeoDataProcess',
        'GetDataset',
        'GetDatasetTableRepresentation',
        'GetDatasetSites',
      ],
    });
  };

  const startDatasetPreProcessing = async (input: IPreProcessDatasetInput) => {
    return await _preProcessDataset({
      variables: {
        input,
      },
      refetchQueries: [
        'GetGeoDataProcess',
        'GetDataset',
        'GetDatasetTableRepresentation',
        'GetDatasetSites',
      ],
      fetchPolicy: 'no-cache',
    });
  };

  const createDataset = async (
    value: Pick<IGeoUploadFormValues, 'title' | 'rawMaterialId' | 'countryCode' | 'ownedBy'> & {
      files: { id: string }[];
    },
    geoDataProcessId?: string
  ) =>
    await _createDataset({
      variables: {
        input: {
          title: value.title,
          originCountry: value.countryCode,
          rawMaterialId: value.rawMaterialId,
          ownedById: value.ownedBy.id,
          documentIds: value.files.map(({ id }) => id),
          geoDataProcessIds: geoDataProcessId ? [geoDataProcessId] : undefined,
          dataType: 'GEODATA',
        },
      },
    });

  const updateDataset = async (
    value: Partial<
      Pick<IGeoUploadFormValues, 'title' | 'rawMaterialId' | 'countryCode' | 'ownedBy'>
    > & {
      datasetId: string; // datasetid is required
      files?: { id: string }[];
    }
  ) =>
    await _updateDataset({
      variables: {
        id: value.datasetId,
        input: {
          title: value.title,
          originCountry: value.countryCode,
          rawMaterialId: value.rawMaterialId,
          ownedById: value.ownedBy?.id,
          documentIds: value.files?.map(({ id }) => id),
        },
      },
      refetchQueries: ['GetGeoDataProcess'],
    });

  const deleteDataset = async (dataset: { id: string; documents?: { id: string }[] }) => {
    try {
      // TODO: move the document deletion to the backend
      if (dataset.documents?.length) {
        deleteDocument({ variables: { ids: dataset.documents.map(({ id }) => id) } });
      }
      return await _deleteDataset({ variables: { id: dataset.id } });
    } catch (error) {
      console.error('Something went wrong deleting datasets', error);
    }
  };

  const [_createGeoEntitiesExport] = useMutation<
    { createGeoEntitiesExport: { fileUrl: string } },
    { input: ICreateGeoEntitiesExportInput }
  >(CREATE_GEO_ENTITIES_EXPORT);

  const createGeoEntitiesExport = async (input: ICreateGeoEntitiesExportInput) =>
    _createGeoEntitiesExport({
      variables: {
        input,
      },
    });

  return {
    updateDataset,
    createDataset,
    deleteDataset,
    startDatasetProcessing,
    startDatasetPreProcessing,
    createGeoEntitiesExport,
  };
};

export default useDatasetMutation;
